export const CANONICAL_ROLES = [
  'org_owner',
  'trading_operator',
  'analyst',
  'auditor',
  'rugira_platform_admin',
  'rugira_support',
  'rugira_compliance_admin'
] as const;

export const ROLE_LABELS: Record<string, string> = {
  org_owner: 'Admin',
  trading_operator: 'Trader',
  analyst: 'Analyst',
  auditor: 'Auditor',
  rugira_platform_admin: 'Platform Admin',
  rugira_support: 'Support',
  rugira_compliance_admin: 'Compliance Admin'
};

const CANONICAL_SET = new Set<string>(CANONICAL_ROLES);

const normalizeRoleKey = (role: string): string =>
  role
    .trim()
    .replace(/[\s-]+/g, '_')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase();

const ROLE_ALIASES: Record<string, typeof CANONICAL_ROLES[number]> = {
  org_owner: 'org_owner',
  orgowner: 'org_owner',
  admin: 'org_owner',
  trading_operator: 'trading_operator',
  tradingoperator: 'trading_operator',
  trader: 'trading_operator',
  analyst: 'analyst',
  auditor: 'auditor',
  readonly: 'auditor',
  read_only: 'auditor',
  rugira_platform_admin: 'rugira_platform_admin',
  rugiraplatformadmin: 'rugira_platform_admin',
  rugiraplatform_admin: 'rugira_platform_admin',
  rugira_support: 'rugira_support',
  rugirasupport: 'rugira_support',
  support: 'rugira_support',
  rugira_compliance_admin: 'rugira_compliance_admin',
  rugiracomplianceadmin: 'rugira_compliance_admin',
  compliance_admin: 'rugira_compliance_admin',
  complianceadmin: 'rugira_compliance_admin',
};

export const canonicalizeRole = (role: string | undefined | null): string | undefined => {
  if (!role || typeof role !== 'string') {
    return undefined;
  }
  const normalized = normalizeRoleKey(role);
  if (!normalized) {
    return undefined;
  }
  if (CANONICAL_SET.has(normalized)) {
    return normalized;
  }
  if (ROLE_ALIASES[normalized]) {
    return ROLE_ALIASES[normalized];
  }
  return normalized;
};

export const canonicalizeRoles = (roles: unknown): string[] => {
  if (!Array.isArray(roles)) {
    return [];
  }
  const result: string[] = [];
  const seen = new Set<string>();
  roles.forEach((role) => {
    const mapped = canonicalizeRole(typeof role === 'string' ? role : undefined);
    if (mapped && !seen.has(mapped)) {
      seen.add(mapped);
      result.push(mapped);
    }
  });
  return result;
};

export const hasRole = (roles: string[] | undefined | null, target: string): boolean => {
  if (!Array.isArray(roles)) return false;
  const canonicalTarget = canonicalizeRole(target);
  return canonicalTarget ? roles.includes(canonicalTarget) : false;
};

export const rolePriorityList = [
  'rugira_platform_admin',
  'org_owner',
  'rugira_compliance_admin',
  'trading_operator',
  'analyst',
  'auditor',
  'rugira_support'
] as const;

export const pickHighestPriorityRole = (roles: string[] | undefined | null): string | null => {
  if (!Array.isArray(roles) || roles.length === 0) return null;
  for (const candidate of rolePriorityList) {
    if (roles.includes(candidate)) {
      return candidate;
    }
  }
  return roles[0] || null;
};

export const describeRole = (role: string): string =>
  ROLE_LABELS[role] || role.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
